/**
 * TopupModal.tsx — Reusable Stripe-based credit top-up modal
 *
 * Uses Stripe Payment Intents with embedded card input (no redirect).
 * Can be embedded in both rule42-billing admin and rule42-cloud.
 *
 * Flow:
 *   1. User enters amount
 *   2. Frontend calls POST /credits/topup/payment-intent to get clientSecret
 *   3. Stripe.js confirms payment with card details
 *   4. On success, Stripe webhook grants credits automatically
 *   5. UI polls balance to show updated amount
 */
import React, { useState, useEffect, useRef } from 'react';

// ─── Stripe.js loader (CDN) ──────────────────────────────────────────

let stripePromise: Promise<unknown> | null = null;

function loadStripeScript(): Promise<unknown> {
  if (stripePromise) return stripePromise;
  if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>)['Stripe']) {
    return Promise.resolve((window as unknown as Record<string, unknown>)['Stripe']);
  }
  stripePromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve((window as unknown as Record<string, unknown>)['Stripe']);
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
  return stripePromise;
}

// ─── Types ───────────────────────────────────────────────────────────

interface TopupModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  customerName?: string;
  apiBaseUrl: string;
  token: string;
  onSuccess?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────

export default function TopupModal({ open, onClose, customerId, customerName, apiBaseUrl, token, onSuccess }: TopupModalProps): React.JSX.Element | null {
  const [amount, setAmount] = useState(100);
  const [step, setStep] = useState<'amount' | 'payment' | 'processing' | 'success' | 'error'>('amount');
  const [errorMessage, setErrorMessage] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [stripeInstance, setStripeInstance] = useState<unknown>(null);
  const [cardElement, setCardElement] = useState<unknown>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setStep('amount');
      setAmount(100);
      setErrorMessage('');
      setClientSecret('');
      setCardElement(null);
    }
  }, [open]);

  // Create PaymentIntent and initialize Stripe Elements when entering payment step
  async function startPayment(): Promise<void> {
    setStep('processing');
    setErrorMessage('');

    try {
      const res = await fetch(`${apiBaseUrl}/credits/topup/payment-intent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          amountCents: Math.round(amount * 100),
          customerName,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed to create payment intent: ${res.status}`);
      }

      const data = await res.json() as { clientSecret: string; publishableKey: string; paymentIntentId: string };
      setClientSecret(data.clientSecret);
      setPublishableKey(data.publishableKey);

      // Load Stripe.js
      const StripeConstructor = await loadStripeScript() as (key: string) => unknown;
      const stripe = StripeConstructor(data.publishableKey);
      setStripeInstance(stripe);

      // Create card element
      const elements = (stripe as { elements: (opts: { clientSecret: string; appearance?: Record<string, unknown> }) => unknown }).elements({
        clientSecret: data.clientSecret,
        appearance: { theme: 'stripe' },
      });

      const card = (elements as { create: (type: string) => { mount: (sel: string | HTMLElement) => void; on: (event: string, cb: (e: unknown) => void) => void } }).create('card');
      setCardElement(card);

      setStep('payment');

      // Mount after render
      setTimeout(() => {
        if (cardContainerRef.current) {
          card.mount(cardContainerRef.current);
        }
      }, 100);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      setStep('error');
    }
  }

  // Confirm payment with Stripe
  async function confirmPayment(): Promise<void> {
    if (!stripeInstance || !cardElement) return;
    setStep('processing');
    setErrorMessage('');

    try {
      const result = await (stripeInstance as {
        confirmCardPayment: (secret: string, opts: { payment_method: { card: unknown } }) => Promise<{ error?: { message: string }; paymentIntent?: { status: string } }>
      }).confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.paymentIntent?.status === 'succeeded') {
        setStep('success');
        // Wait a moment for webhook to process, then notify parent
        setTimeout(() => {
          onSuccess?.();
        }, 2000);
      } else {
        throw new Error(`Payment status: ${result.paymentIntent?.status ?? 'unknown'}`);
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Payment failed');
      setStep('error');
    }
  }

  if (!open) return null;

  const amountCents = Math.round(amount * 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => step !== 'processing' && onClose()}>
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Purchase Credits</h3>
          {step !== 'processing' && (
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
          )}
        </div>

        {/* Customer info */}
        <div className="mb-4 rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Account</p>
          <p className="text-sm font-medium text-slate-900">{customerName ?? customerId.slice(0, 8)}</p>
        </div>

        {/* Step: Amount */}
        {step === 'amount' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Amount (USD)</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {[25, 50, 100, 250].map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setAmount(preset)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                      amount === preset ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    ${preset}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2 text-lg"
              />
              <p className="mt-1 text-xs text-slate-400">Credits will be added to your balance after payment completes.</p>
            </div>
            <button
              onClick={() => void startPayment()}
              disabled={amount < 1}
              className="w-full rounded-lg bg-cyan-600 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              Continue to Payment · ${amount.toFixed(2)}
            </button>
          </div>
        )}

        {/* Step: Payment (card input) */}
        {step === 'payment' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Card Details</label>
              <div
                ref={cardContainerRef}
                className="mt-2 rounded-lg border border-slate-300 p-3"
                style={{ minHeight: '50px' }}
              />
              <p className="mt-1 text-xs text-slate-400">🔒 Payment secured by Stripe. Your card details are never stored.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setStep('amount')}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
              <button
                onClick={() => void confirmPayment()}
                className="flex-1 rounded-lg bg-cyan-600 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700"
              >
                Pay ${amount.toFixed(2)}
              </button>
            </div>
          </div>
        )}

        {/* Step: Processing */}
        {step === 'processing' && (
          <div className="flex flex-col items-center py-8">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-cyan-600" />
            <p className="mt-4 text-sm text-slate-600">Processing payment…</p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="flex flex-col items-center py-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-4 text-lg font-semibold text-slate-900">Payment Successful!</p>
            <p className="mt-1 text-sm text-slate-500">${amount.toFixed(2)} in credits added to your balance.</p>
            <button
              onClick={onClose}
              className="mt-6 w-full rounded-lg bg-cyan-600 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-700"
            >
              Done
            </button>
          </div>
        )}

        {/* Step: Error */}
        {step === 'error' && (
          <div className="flex flex-col items-center py-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="mt-4 text-lg font-semibold text-slate-900">Payment Failed</p>
            <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
            <div className="mt-6 flex w-full gap-3">
              <button
                onClick={() => setStep('amount')}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Try Again
              </button>
              <button
                onClick={onClose}
                className="flex-1 rounded-lg bg-slate-100 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
